import React, { useContext, useEffect, useRef, useState } from "react";
import { clearInviteCode, readInviteCode } from "../../../lib/referralInvite";
import { markNewSignupForWallet } from "../../../lib/walletMoment";
import { useNavigate } from "react-router-dom";
import { IG_STEP, OnboardingData } from "../../../hooks/useOnboardingState";
import { getCsrf } from "../../../api";
import { authApi } from "../../../services/auth";
import { countriesApi } from "../../../services/countries";
import { usersApi } from "../../../services/users";
import type { RegisterFields, RegisterResponse } from "../../../types/auth";
import { AuthContext } from "../../../context/AuthContext";
import { fetchCountryData, FALLBACK_COUNTRIES, SupportedCountry } from "../../../lib/supportedCountries";
import GoogleButton from "../../../components/GoogleButton";
import { ConnectScreen } from "../../../components/connect/ConnectScreen";
import CountryPicker from "../../../components/connect/CountryPicker";
import { MasspayRail, NotYetRail, StripeRail } from "../../../components/connect/RailScreens";
import { signInWithApple, AppleSignInResult } from "../../../lib/appleSignIn";
import { isTokenExpiringSoon, tokenEmail } from "../../../lib/jwtExpiry";
import { track } from "../../../lib/analytics";
import { getDeviceId } from "../../../lib/biometricSignIn";
import {
  openStripeUrl,
  stripeClientFields,
  onStripeBrowserClosed,
  stashAppStripeNonce,
  getAppStripeNonce,
  wasStripeReturnJustHandled,
} from "../../../lib/stripeFlow";
import { t } from "../../../i18n";
import { T } from "../../../i18n/T";

// The app-tips box the sign-in screen showed (its wording's version) and how
// the talent left it, as the register call carries it (server #375, #377):
// "Don't email me app tips" sends emailTipsOptOut, "Email me app tips" sends
// emailTipsOptIn. No box (none where the law needs no choice, they left
// before it came, or a sign-up saved before the box existed) sends nothing:
// the server records nothing and its country rules decide.
function tipsBoxAnswer(data: OnboardingData): Pick<RegisterFields, "emailNotice" | "emailTipsOptOut" | "emailTipsOptIn"> {
  if (data.emailNotice === "signup-2026-10-optin") return { emailNotice: data.emailNotice, emailTipsOptIn: data.emailTipsOptIn === true };
  if (data.emailNotice === "signup-2026-10") return { emailNotice: data.emailNotice, emailTipsOptOut: data.emailTipsOptOut === true };
  return {};
}

interface Props {
  data: OnboardingData;
  updateData: (updates: Partial<OnboardingData>) => void;
  clearState: () => void;
  clearPersistedStorage: () => void;
  goToStep: (step: number, withIgStep?: boolean) => void;
  onBack: () => void;
}

// On the "Connect bank" path we redirect to Stripe before the talent is
// logged in. We deliberately keep this page mounted during the redirect
// (clearPersistedStorage() wipes only the saved localStorage draft, not the
// in-memory step) so the screen doesn't flash back to Step 1 while Stripe
// loads. The in-memory avatar File dies on the page unload, so we stash a
// base64 copy here for StripeCallbackPage to finish the upload once the user
// comes back authenticated.
// Mirrored in StripeCallbackPage.tsx — keep keys in sync.
const PENDING_AVATAR_KEY = "pendingAvatarUpload";

export default function Step6Complete({ data, updateData, clearState, clearPersistedStorage, goToStep }: Props) {
  // Final step of onboarding. Build 41 removed the access-code interstitial,
  // so nothing here redeems a code: the server creates the account
  // unactivated and it waits for the founder's Approve in the Approvals tab
  // (THE IG GATE, server lib/approval.js) — exactly what skipping the code
  // already did. The register endpoints still accept an `accessCode`, so the
  // VIP fast path stays available server-side if we ever want to hand one out
  // through another door.
  //
  // Two ways out of this step:
  //   Connect Bank  register → redirect to Stripe → return to
  //                 /register/stripe/callback (handles login + navigation).
  //                 A MassPay country (Indonesia first) has no Stripe round
  //                 trip: the server signs the talent in on the spot, like
  //                 Skip, and MassPay is set up from the app.
  //   Skip for now  FREE TIER: register with skipBank → the server creates the
  //                 account with no bank and signs the talent in on the spot →
  //                 straight to Requests. Free requests only until they connect
  //                 a bank (any money button) and the founder approves.
  //                 Continue on a country that isn't open yet does the same,
  //                 after putting their sign-in email on its waitlist.
  // Before the Stripe redirect
  // we call clearPersistedStorage() (localStorage only); the in-memory step is
  // intentionally left intact so the page keeps showing "Redirecting to Stripe…"
  // instead of flashing back to Step 1. clearState() (a full reset) is used only
  // on the dev-mode fallback and the Continue-to-Profile paths below.
  const needsAuth = !data.authMethod;
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  // Bank country is asked here, at the "Connect Bank" tap, because Stripe locks
  // it permanently at account creation (the register call below). The full
  // screen (1.5.2, the same steps as "Connect your bank" in the app) holds the
  // pick in local state until the talent confirms who pays them.
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [countryStep, setCountryStep] = useState<"country" | "rail">("country");
  const [bankCountry, setBankCountry] = useState(data.bankCountryName || "");
  // Live country list (open/locked flags) from the server; the hardcoded
  // mirror renders instantly and covers offline.
  const [countries, setCountries] = useState<SupportedCountry[]>(FALLBACK_COUNTRIES);
  // Where the talent's connection is (server, never stored): pre-selected in
  // the picker, never chosen for them.
  const [detectedCode, setDetectedCode] = useState<string | null>(null);
  // Set when the SERVER rejected a country as locked (stale client list) —
  // forces the "not open yet" card even if our local copy thought it was open.
  const [serverLockedCountry, setServerLockedCountry] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchCountryData().then((d) => {
      if (!alive) return;
      setCountries(d.countries);
      setDetectedCode(d.detectedCode);
    });
    return () => {
      alive = false;
    };
  }, []);

  const selectedCountry = countries.find((c) => c.name === bankCountry) || null;
  const countryIsLocked =
    !!selectedCountry && (!selectedCountry.open || selectedCountry.name === serverLockedCountry);
  // The Google/Apple sign-in token captured at Step 1 expires ~1h after issue,
  // but is only spent here at register time. When it's stale we flip into a
  // re-auth panel (one tap to re-mint) instead of dead-ending on a server
  // rejection. reauthCountRef caps retries so a genuinely bad token can't loop.
  const [reauthNeeded, setReauthNeeded] = useState(false);
  const reauthCountRef = useRef(0);
  // Which way out the talent chose — remembered across a re-auth retry, so a
  // fresh sign-in token finishes the SAME choice. (Skip needs a Google or Apple
  // sign-in: those are the two doors the server opens without a bank.)
  const skipBankRef = useRef(false);
  const canSkipBank = data.authMethod === "google" || data.authMethod === "apple";
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const registerUser = async (override?: { google?: string; apple?: AppleSignInResult; bankCountryName?: string }) => {
    setPending(true);
    setError("");

    // `override` carries a just-re-minted token from the re-auth panel. When
    // present we trust it and skip the proactive expiry check below — both to
    // avoid a redundant re-prompt and to sidestep a re-auth loop if the local
    // clock is skewed (let the server be the final judge of a fresh token).
    const usingFreshToken = !!(override?.google || override?.apple);
    const googleCredential = override?.google || data.googleCredential;
    const appleCredential = override?.apple
      ? { idToken: override.apple.idToken, user: override.apple.user }
      : data.appleCredential;

    if (!usingFreshToken) {
      if (data.authMethod === "google" && isTokenExpiringSoon(googleCredential)) {
        setReauthNeeded(true);
        setPending(false);
        return;
      }
      if (data.authMethod === "apple" && isTokenExpiringSoon(appleCredential?.idToken)) {
        setReauthNeeded(true);
        setPending(false);
        return;
      }
    }

    try {
      await getCsrf();

      const payload: RegisterFields = {
        name: data.name,
        slug: data.slug,
        // Phase C: no price/currency in the register payload — the server
        // derives the currency from the bank country and seeds a placeholder
        // price; the DJ chooses their real price in the first-sign-in popup.
        // Prefer the country handed in directly from the confirm popup: the
        // updateData() in handleConfirmCountry hasn't flushed into `data` yet
        // on this same tick. Stripe locks the bank country at account creation,
        // so a stale/empty value here would create the account in the wrong
        // country with no way to fix it.
        bankCountryName: skipBankRef.current ? undefined : override?.bankCountryName ?? data.bankCountryName,
        // FREE TIER: no bank country, no Stripe account; signed in on the spot.
        ...(skipBankRef.current ? { skipBank: true } : {}),
        // Venue and bio are not asked at sign-up any more (founder, 1.5.1
        // #1); both are optional server-side and set later in Edit profile.
        // With the Connect Instagram step (build 54) nothing typed goes: the
        // Instagram it proved rides as igClaim, which never blocks sign-up.
        instagram: data.igStep ? undefined : data.instagram || undefined,
        ...(data.igStep && data.igUsername && data.igClaim ? { igClaim: data.igClaim } : {}),
        email: data.email || undefined,
        // Refer & Earn / launch offer: who invited them. Optional; a wrong code
        // is ignored server-side. Falls back to the code a /join link saved: a
        // sign-up resumed at this step never passed the profile step that
        // copies it into `data`, and the server stamps the 0% launch offer only
        // here, at sign-up (2026-10-10).
        referralCode: data.referralCode || readInviteCode() || undefined,
        // Fraud door: the same stable per-device id biometric sign-in
        // uses. The server caps signups per device per day (lib/signupGuard.js);
        // absent → that one cap is skipped, the IP caps still apply.
        deviceId: getDeviceId(),
        // Native app: tags the minted Stripe link's return_url with app=1 so
        // the return trip hands off back into the app (see lib/stripeFlow.ts).
        ...stripeClientFields(),
        // App-tips emails: the sign-in screen's box and how they left it.
        // Every door and the re-auth retry come through here.
        ...tipsBoxAnswer(data),
      };

      let response: RegisterResponse;

      if (data.authMethod === "google" && googleCredential) {
        response = await authApi.googleRegister({
          credential: googleCredential,
          ...payload,
        });
      } else if (data.authMethod === "apple" && appleCredential) {
        response = await authApi.appleRegister({
          idToken: appleCredential.idToken,
          ...payload,
        });
      } else {
        throw new Error(t("step6Complete.noAuthenticationMethodSelected"));
      }

      // Growth funnel: the account now exists server-side (account_created is
      // logged there); the Stripe leg starts now.
      track("onboarding_started", { props: { method: data.authMethod } });
      // Refer & Earn: the invite did its job. Forget it, so a different person
      // signing up on this device later isn't credited to the same talent.
      clearInviteCode();
      // A brand-new account: its Apple Wallet step follows the Face ID and
      // notification asks (lib/walletMoment).
      markNewSignupForWallet();

      // Server returns a hosted Stripe onboarding link and expects the talent
      // to come back via /register/stripe/callback. Two pre-redirect steps:
      //   1. Stash the avatar in localStorage — the in-memory File object dies
      //      with the page unload. StripeCallbackPage finishes the upload once
      //      the talent has a token + the server-canonical slug from
      //      /auth/user (the User model's pre-validate hook re-derives slug
      //      from name, so the locally-chosen slug isn't authoritative).
      //   2. clearPersistedStorage() before redirecting — onboarding ends here.
      //      The access code already redeemed at register time, and the user is
      //      coming back authenticated, so RequireNoAuth will route them to
      //      /profile regardless of what's in onboarding state. We wipe the
      //      saved localStorage so stale data doesn't persist forever — but we
      //      deliberately do NOT reset the in-memory step (clearState would),
      //      because that flashes the page back to Step 1 ("Sign up with
      //      Google/Apple") for the second or two the browser spends loading
      //      the Stripe URL. `pending` stays true, so the purple button keeps
      //      showing "Redirecting to Stripe…" right up until the page unloads.
      if (response?.stripeUrl) {
        // avatarPreview is the cropped photo as a base64 data URL. We stash
        // that (not a File) because it survives a mid-onboarding page reload —
        // a File object does not, and losing it silently skipped the upload.
        if (data.avatarPreview) {
          try {
            localStorage.setItem(
              PENDING_AVATAR_KEY,
              JSON.stringify({
                dataUrl: data.avatarPreview,
                name: "avatar.png",
                ts: Date.now(),
                // Whose photo this is: the account just created (the server
                // takes its email from this same sign-in token). The callback
                // uploads it to that account only — never to the next one to
                // finish Stripe on this device (2026-10-10).
                email: tokenEmail(data.authMethod === "apple" ? appleCredential?.idToken : googleCredential),
              })
            );
          } catch (err) {
            // Best-effort: quota errors just mean the talent will re-upload
            // from /profile. Don't block the Stripe redirect.
            console.error("Failed to stash avatar before Stripe redirect:", err);
          }
        }
        // Native: keep the app's own copy of the one-time completion key so
        // the sheet-close handler below can finish the round trip in-app.
        if (response.appNonce) stashAppStripeNonce(response.appNonce);
        clearPersistedStorage();
        // Web: full redirect (page unloads here). Native: Stripe opens in the
        // in-app browser sheet ON TOP of this page. When the sheet closes we
        // SELF-COMPLETE with the app-held nonce — this covers "finished
        // Stripe but never tapped the hand-off card" (the stranded path): the
        // callback page silently logs in / resumes / shows the Almost-there
        // card, and uploads the stashed avatar from THIS webview's storage,
        // its only home. If the deep link already routed the return a moment
        // ago, skip (double-navigation guard); the talent who merely bailed
        // gets the same page's friendly re-entry instead of a dead button.
        const stopBrowserWatch = onStripeBrowserClosed(() => {
          stopBrowserWatch();
          const heldNonce = getAppStripeNonce();
          if (heldNonce && !wasStripeReturnJustHandled()) {
            navigate(`/register/stripe/callback?nonce=${encodeURIComponent(heldNonce)}`, { replace: true });
            return;
          }
          setPending(false);
        });
        await openStripeUrl(response.stripeUrl);
        return;
      }

      // No Stripe link: the talent skipped the bank (FREE TIER), or this is
      // the dev-mode fallback. Either way the server signed them in.
      if (response?.accessToken && response?.user) {
        login(response.user, response.accessToken);
      }

      if (data.avatarPreview && response?.user?.slug) {
        try {
          const blob = await (await fetch(data.avatarPreview)).blob();
          const formData = new FormData();
          formData.append("avatar", blob, "avatar.png");
          await usersApi.update(response.user.slug, formData);
        } catch (err) {
          console.error("Avatar upload failed during registration:", err);
        }
      }

      clearState();
      // Land in the app, not on a guard's default: the first thing a new
      // talent sees is their Requests page (the practice request lives there).
      // A MassPay country (no Stripe round trip) lands there too; its payout
      // set-up starts from any money button, after the first-run tour.
      if (skipBankRef.current || response?.payoutRail === "masspay") navigate("/requests", { replace: true });
    } catch (e: any) {
      const msg = e?.response?.data?.msg;
      // msgEn: the server's English (msg may be translated, src/i18n/serverMessages).
      const msgEn = e?.response?.data?.msgEn ?? msg;
      track("signup_failed", { errorReason: msgEn || e?.message || "register_failed" });
      // The Instagram step switched on after the sign-in check (the server
      // now refuses a typed handle): quietly to that step, the box emptied.
      if (e?.response?.data?.code === "instagram_verify_required") {
        updateData({ instagram: "", igStep: true });
        goToStep(IG_STEP, true);
        setPending(false);
        return;
      }
      // Country has no working payout rail — the server is the final gate
      // (our local list may be stale). Reopen the popup on the locked
      // country, which offers the free-requests way in instead of an error
      // dead-end.
      if (e?.response?.data?.code === "country_not_open") {
        setServerLockedCountry(e.response.country || override?.bankCountryName || data.bankCountryName || null);
        setCountryStep("rail");
        setPending(false);
        setShowCountryModal(true);
        return;
      }
      // Safety net for the case the proactive check missed (clock skew, or the
      // token died in flight): the server rejected the sign-in token as stale.
      // Recover with a fresh sign-in rather than surfacing a raw error code.
      if (
        (msg === "invalid_google_credential" || msg === "invalid_apple_token") &&
        reauthCountRef.current < 2
      ) {
        setReauthNeeded(true);
        setPending(false);
        return;
      }
      // "Email already in use" keeps its English value: it picks the sign-in hint below.
      setError(msgEn === "Email already in use" ? msgEn : msg || t("step6Complete.registrationFailedPleaseTry"));
      setPending(false);
    }
  };

  const handleReauthGoogle = (credential: string) => {
    reauthCountRef.current += 1;
    updateData({ googleCredential: credential });
    setReauthNeeded(false);
    registerUser({ google: credential });
  };

  const handleReauthApple = async () => {
    setError("");
    setPending(true);
    try {
      const result = await signInWithApple();
      if (!result?.idToken) {
        setPending(false);
        return;
      }
      reauthCountRef.current += 1;
      updateData({ appleCredential: { idToken: result.idToken, user: result.user } });
      setReauthNeeded(false);
      registerUser({ apple: result });
    } catch (err: any) {
      // Ignore user-cancelled popups; surface anything else.
      if (err?.message !== "The user canceled the sign-in flow." && err?.error !== "popup_closed_by_user") {
        setError(t("step6Complete.appleSignInFailed"));
      }
      setPending(false);
    }
  };

  const handleSkipBank = () => {
    setError("");
    skipBankRef.current = true;
    registerUser();
  };

  const handleConnect = () => {
    if (!data.authMethod) {
      goToStep(1);
      return;
    }
    skipBankRef.current = false;
    // Stripe permanently locks the bank country at account creation — which is
    // exactly this tap. So we ask for + confirm the country in a popup here,
    // before the register call runs.
    setError("");
    setCountryStep("country");
    setShowCountryModal(true);
  };

  // A country that isn't open yet (founder 2026-10-14: the email box was
  // confusing). One tap: their sign-in email goes on that country's waitlist,
  // then sign-up finishes on free requests, exactly like Skip for now. The
  // waitlist is best effort: it never stands between a talent and their page.
  const handleLockedContinue = async () => {
    if (!selectedCountry) return;
    setShowCountryModal(false);
    setError("");
    skipBankRef.current = true;
    setPending(true);
    // Apple sends the email only on the first sign-in; the token still has it.
    const token = data.authMethod === "apple" ? data.appleCredential?.idToken : data.googleCredential;
    const email = (data.email || tokenEmail(token)).trim();
    if (email) {
      try {
        await getCsrf();
        await countriesApi.joinWaitlist(email, selectedCountry.name);
      } catch {
        // Sign-up goes on without it.
      }
    }
    registerUser();
  };

  const closeCountrySheet = () => setShowCountryModal(false);

  // The picker: one tap on a country moves on to who pays them.
  const pickCountry = (c: SupportedCountry) => {
    setBankCountry(c.name);
    setCountryStep("rail");
  };
  const backToPicker = () => setCountryStep("country");

  const handleConfirmCountry = () => {
    if (!bankCountry) return; // Confirm is disabled until a country is picked.
    if (countryIsLocked) return; // Locked countries go through handleLockedContinue.
    setShowCountryModal(false);
    // Persist it (the re-auth retry path reads data.bankCountryName), AND hand
    // it straight into register so the first attempt has it even before this
    // updateData() flushes to `data`.
    updateData({ bankCountryName: bankCountry });
    registerUser({ bankCountryName: bankCountry });
  };

  const isLoading = pending;

  return (
    <div className="onboarding-step step5-complete">
      <div className="ob-orb-wrap" aria-hidden="true">
        <span className="ob-spark ob-spark-1">✦</span>
        <span className="ob-orb">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 10h18" /><path d="M5 6l7-3 7 3" /><path d="M4 10v11" /><path d="M20 10v11" /><path d="M8 14v4" /><path d="M12 14v4" /><path d="M16 14v4" /></svg>
        </span>
        <span className="ob-spark ob-spark-2">✦</span>
      </div>
      <h2 className="ob-title"><T k="step6Complete.connectYourBankTo" tags={{ 0: <br /> }} /></h2>
      <p className="ob-sub">{t("step6Complete.takesAboutMinutes")}</p>

      <div className="ob-card">
        <div className="ob-card-avatar">
          {data.avatarPreview ? <img src={data.avatarPreview} alt={t("step6Complete.yourAvatar")} /> : data.name?.[0]?.toUpperCase() || "?"}
        </div>
        <div className="ob-card-info">
          <h3 className="ob-card-name">{data.name || t("step6Complete.yourName")}</h3>
        </div>
        <button type="button" className="ob-card-edit" onClick={() => goToStep(2)} disabled={isLoading}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
          {t("step6Complete.edit")}
        </button>
      </div>

      <div className="ob-tiles">
        <div className="ob-tile">
          <span className="ob-tile-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
          </span>
          <span className="ob-tile-label">{t("step6Complete.shareYourLink")}</span>
        </div>
        <span className="ob-tile-div" aria-hidden="true" />
        <div className="ob-tile">
          <span className="ob-tile-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>
          </span>
          <span className="ob-tile-label">{t("step6Complete.getRequests")}</span>
        </div>
        <span className="ob-tile-div" aria-hidden="true" />
        <div className="ob-tile">
          <span className="ob-tile-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" /><path d="M12 18V6" /></svg>
          </span>
          <span className="ob-tile-label">{t("step6Complete.getPaid")}</span>
        </div>
      </div>

      {error === "Email already in use" ? (
        <p className="step5-error" role="alert">
          <T k="step6Complete.emailAlreadyInUse" tags={{ 0: (c) => <button
            type="button"
            className="step5-error-login-inline"
            onClick={() => navigate("/login")}
          >{c}</button> }} />
        </p>
      ) : error ? (
        <p className="step5-error" role="alert">{error}</p>
      ) : null}

      {needsAuth ? (
        <div className="step5-actions">
          <button
            type="button"
            className="step5-choice step5-choice--primary"
            onClick={() => goToStep(1)}
          >
            {t("step6Complete.signInToComplete")}
          </button>
          <button
            type="button"
            className="step5-skip-link"
            onClick={() => {
              clearState();
              navigate("/profile", { replace: true });
            }}
          >
            {t("step6Complete.continueToProfile")}
          </button>
        </div>
      ) : reauthNeeded ? (
        <div className="step5-reauth">
          <p className="step5-reauth-msg">
            {t("step6Complete.yourSignInTimed")}
          </p>
          {data.authMethod === "google" ? (
            <GoogleButton
              onSuccess={handleReauthGoogle}
              onError={() => setError(t("step6Complete.googleSignInFailed"))}
              text="continue_with"
            />
          ) : data.authMethod === "apple" ? (
            <button
              type="button"
              className="step1-apple-btn"
              onClick={handleReauthApple}
              disabled={pending}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.51-3.23 0-1.44.64-2.2.45-3.06-.4C3.79 16.17 4.36 9.02 8.82 8.78c1.15.06 1.95.65 2.63.68.99-.2 1.95-.78 3.01-.7 1.28.1 2.24.61 2.87 1.56-2.64 1.57-2.01 5.02.36 5.98-.49 1.3-1.13 2.58-2.64 3.98zM12.03 8.7c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              {pending ? t("step6Complete.continuing") : t("step6Complete.continueWithApple")}
            </button>
          ) : null}
        </div>
      ) : (
        <div className="ob-footer">
          <button type="button" className="ob-cta ob-cta--purple" onClick={handleConnect} disabled={isLoading} aria-busy={pending && !skipBankRef.current}>
            <svg className="ob-cta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 10h18" /><path d="M5 6l7-3 7 3" /><path d="M4 10v11" /><path d="M20 10v11" /><path d="M8 14v4" /><path d="M12 14v4" /><path d="M16 14v4" /></svg>
            {pending && !skipBankRef.current ? t("step6Complete.redirectingToStripe") : t("step6Complete.connectBank")}
            <svg className="ob-cta-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
          </button>
          {canSkipBank && (
            <button type="button" className="ob-cta ob-cta--quiet" onClick={handleSkipBank} disabled={isLoading} aria-busy={pending && skipBankRef.current}>
              <span>{pending && skipBankRef.current ? t("step6Complete.settingUpYourPage") : <><T k="step6Complete.skipForNowFree" tags={{ 0: (c) => <b>{c}</b> }} /></>}</span>
              <svg className="ob-cta-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
            </button>
          )}
          {/* No terms line here: the sign-in screen (Step1Welcome) carries
              "By continuing, you agree…" and comes before the account exists. */}
          <p className="ob-trust">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
            <T k="step6Complete.bankLevelEncryptionPowered" tags={{ 0: (c) => <b>{c}</b> }} />
          </p>
        </div>
      )}

      {/* The payout country, full screen (1.5.2, founder 2026-10-19): the same
          steps as "Connect your bank" in the app — the country (their location
          pre-selected), then who pays them; Continue registers. */}
      {showCountryModal && (
        <ConnectScreen
          progress={countryStep === "country" ? 25 : 50}
          onClose={closeCountrySheet}
          onBack={countryStep === "rail" ? backToPicker : undefined}
          busy={pending}
        >
          {countryStep === "country" || !selectedCountry ? (
            <CountryPicker countries={countries} selectedCode={selectedCountry?.code || detectedCode} onPick={pickCountry} pending={pending} />
          ) : countryIsLocked ? (
            <NotYetRail country={selectedCountry} onNotify={handleLockedContinue} onOther={backToPicker} pending={pending} />
          ) : selectedCountry.rail === "masspay" ? (
            <MasspayRail country={selectedCountry} onChange={backToPicker} onContinue={handleConfirmCountry} pending={pending} />
          ) : (
            <StripeRail country={selectedCountry} onChange={backToPicker} onContinue={handleConfirmCountry} pending={pending} />
          )}
        </ConnectScreen>
      )}
    </div>
  );
}
